import {
  COUNTRY_CODES,
  SUPPORT_ACTIVITIES,
  TABLES_KEYS,
  TRACKING_CASH_IN_METRICS
} from 'features/command-center/constants';
import { EntityRoutes } from 'routes/constants';
import { hasSingleRole } from 'services/Authorization';
import strings from 'strings';
import { FilterType } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { hasFeatureFlag } from 'UI/utils';
import { OptionRenderers } from 'UI/utils/renderers';

export const PAGE_KEY = TABLES_KEYS.overallPerformanceReport;

const COPIES = strings.commandCenter.dailyPerformance.overallPerformanceReport;

export const FiltersGroups = [
  {
    id: 'recruiters',
    name: 'Recruiters',
    filters: [
      'employeeNumbers',
      'teams',
      'healthStatuses',
      'channelPartnersEmpNumbers',
      'startDate',
      'directors',
      'recruitedBy',
      'lastFeedbackActivityTypes',
      'lastFeedbackActivityDate',
      'countryCode'
    ]
  },
  {
    id: 'sendouts',
    name: 'Sendouts',
    filters: [
      'rangeByCurrentWeekSendouts',
      'rangeByLastWeekSendouts',
      'rangeByThirdWeekSendouts',
      'rangeByFourthWeekSendouts',
      'rangeByFifthWeekSendouts',
      'rangeBySendoutPlacementRatio',
      'rangeByActiveSendouts',
      'rangeByLastFiveWeeksSendouts',
      'rangeByCurrentWeekSendovers',
      'rangeByActiveSendovers'
    ]
  },
  {
    id: 'financial',
    name: 'Financial',
    filters: [
      'rangeByOutstandingBillings',
      'rangeByDrawBalance',
      'rangeByAvgFeePercentage',
      'rangeByAvgFee',
      'rangeByCashin',
      'rangeByCareerCashin',
      'rangeByYearlyProduction',
      'rangeByTrackingProduction',
      'rangeByTrackingCashIn'
    ]
  },
  {
    id: 'metrics',
    name: 'Metrics',
    filters: [
      'rangeByInMails',
      'rangeByInMailsByBillingPeriod',
      'rangeByBulks',
      'rangeByPeopleReached',
      'rangeByPosts',
      'rangeByNewLivePosts',
      'rangeByPhoneTime',
      'rangeByConnectedCalls',
      'rangeByNotConnectedCalls',
      'rangeByTotalCalls'
    ]
  },
  {
    id: 'inventory',
    name: 'Inventory',
    filters: [
      'rangeByNotSignedCompaniesJobOrders',
      'rangeBySignedCompaniesJobOrders',
      'rangeByCandidates',
      'rangeByContacts',
      'rangeByHiringAuthorities',
      'rangeByCompanies'
    ]
  }
];

const LAST_DOC_COLUMNS = !hasSingleRole(Roles.Recruiter)
  ? [
      {
        name: 'lastFeedbackNoteDate',
        label: COPIES.columns.lastDocDate,
        options: {
          sort: true,
          display: true,
          renderer: 'date'
        }
      },
      {
        name: 'lastFeedbackNoteGiverName',
        label: COPIES.columns.lastDocBy,
        options: {
          sort: true,
          display: true
        }
      }
    ]
  : [];

const SUPPORT_ACTIVITY_COLUMNS = !hasSingleRole(Roles.Recruiter)
  ? [
      {
        name: 'lastFeedbackActivityType',
        label: COPIES.columns.supportActivityType,
        options: {
          sort: true,
          display: true
        }
      },
      {
        name: 'lastFeedbackActivityDate',
        label: COPIES.columns.supportActivityDate,
        options: {
          sort: true,
          display: true,
          renderer: 'date'
        }
      },
      {
        name: 'lastFeedbackActivityGiverName',
        label: COPIES.columns.supportActivityBy,
        options: {
          sort: true,
          display: true
        }
      }
    ]
  : [];

const TRACKING_CASH_IN_COLUMNS = hasFeatureFlag(FeatureFlags.CommandCenterTrackingCashIn)
  ? [
      {
        name: 'trackingCashIn',
        label: COPIES.columns.trackingCashIn,
        options: {
          sort: true,
          display: true,
          renderer: 'currency_fixed'
        }
      }
    ]
  : [];

export const COLUMNS = [
  {
    name: 'idFortpac',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'empNo',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'recruiterName',
    label: COPIES.columns.name,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewRecruiter,
          idKey: 'empNo',
          textKey: 'name',
          enableInTabRedirect: true
        }
      },
      setCellHeaderProps: () => ({ className: 'dataTableStickyLeftColumnHeader' }),
      setCellProps: () => ({
        className: 'dataTableStickyLeftColumn'
      })
    }
  },
  {
    name: 'managerName',
    label: COPIES.columns.team,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewTeam,
          idKey: 'managerEmpNo',
          textKey: 'team',
          enableInTabRedirect: true
        }
      }
    }
  },
  {
    name: 'healthStatus',
    label: COPIES.columns.currentHealthStatus,
    options: {
      sort: true,
      display: true,
      renderer: 'healthStatusChip'
    }
  },
  {
    name: 'hireDate',
    label: COPIES.columns.startDate,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'directorName',
    label: COPIES.columns.director,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'teamSupervisorsNames',
    label: COPIES.columns.supportTeam,
    options: {
      sort: false,
      display: true,
      setCellHeaderProps: () => ({
        style: { minWidth: 200 }
      })
    }
  },
  {
    name: 'lastPlacementDate',
    label: COPIES.columns.lastPlacement,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'thisWeekSendouts',
    label: COPIES.columns.currentWeekSendouts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'lastWeekSendouts',
    label: COPIES.columns.lastWeekSendouts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'thirdWeekSendouts',
    label: COPIES.columns.prevWeek2Sendouts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'fourthWeekSendouts',
    label: COPIES.columns.prevWeek3Sendouts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'fifthWeekSendouts',
    label: COPIES.columns.prevWeek4Sendouts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'activeSendouts',
    label: COPIES.columns.activeSendouts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'lastFiveWeeksSendouts',
    label: COPIES.columns.lastFiveWeeksSendouts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'soPlRatio',
    label: COPIES.columns.soToPl,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'currentWeekActiveSendovers',
    label: COPIES.columns.currentWeekSendovers,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'activeSendovers',
    label: COPIES.columns.activeSendovers,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'avgFeePercentage',
    label: COPIES.columns.avgFeePercentageYTD,
    options: {
      sort: true,
      display: true,
      renderer: 'percentage'
    }
  },
  {
    name: 'avgFeeMoney',
    label: COPIES.columns.avgFeeYTD,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'outstandingBillings',
    label: COPIES.columns.currentOutstandingBillings,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'drawBalance',
    label: COPIES.columns.drawBalance,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'cashin',
    label: COPIES.columns.cashInYTD,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'production',
    label: COPIES.columns.productionYTD,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'trackingProduction',
    label: COPIES.columns.trackingProduction,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  ...TRACKING_CASH_IN_COLUMNS,
  {
    name: 'avgInMailsByBillingPeriod',
    label: COPIES.columns.inMails,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'inMailsByBillingPeriod',
    label: COPIES.columns.billingPeriodInMails,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'bulks',
    label: COPIES.columns.bulks,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'peopleReached',
    label: COPIES.columns.peopleReached,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'todayLivePosts',
    label: COPIES.columns.prevDayActivePosts,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'newLivePosts',
    label: COPIES.columns.newLivePosts,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'callsTime',
    label: COPIES.columns.phoneTime,
    options: {
      sort: true,
      display: true,
      renderer: 'phoneTime'
    }
  },
  {
    name: 'calls',
    label: COPIES.columns.calls,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'notConnectedCalls',
    label: COPIES.columns.notConnectedCalls,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'totalCalls',
    label: COPIES.columns.totalCalls,
    options: {
      sort: false,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'notSignedCompaniesJo',
    label: COPIES.columns.notSignedCompaniesJobOrders,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'signedCompaniesJo',
    label: COPIES.columns.signedCompaniesJobOrders,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'candidatesAdded',
    label: COPIES.columns.candidates,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'contactsAdded',
    label: COPIES.columns.contacts,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'haAdded',
    label: COPIES.columns.hiringAuthorities,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'companiesAdded',
    label: COPIES.columns.companies,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'careerCashin',
    label: COPIES.columns.careerCashIn,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  ...SUPPORT_ACTIVITY_COLUMNS,
  {
    name: 'recruitedBy',
    label: COPIES.columns.recruitedBy,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner1Name',
    label: COPIES.columns.channelPartner1,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner2Name',
    label: COPIES.columns.channelPartner2,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'location',
    label: COPIES.columns.location,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'countryCode',
    label: COPIES.columns.countryCode,
    options: {
      sort: true,
      display: true
    }
  },
  ...LAST_DOC_COLUMNS
];

export const ORDER_BY_OPTIONS = {
  column: 'recruiterName',
  direction: 'asc'
};

export const INLINE_FILTERS = ['team', 'healthStatus'];

const FEEDBACK_NOTE_METRICS = !hasSingleRole(Roles.Recruiter)
  ? ['lastFeedbackNoteDate', 'lastFeedbackNoteGiverEmpNo', 'lastFeedbackNoteGiverName']
  : [];

const FEEDBACK_ACTIVITY_METRICS = !hasSingleRole(Roles.Recruiter)
  ? [
      'lastFeedbackActivityType',
      'lastFeedbackActivityGiverEmpNo',
      'lastFeedbackActivityGiverName',
      'lastFeedbackActivityDate'
    ]
  : [];

export const METRICS = [
  'activeSendouts',
  'activeSendovers',
  'avgFeeMoney',
  'avgFeePercentage',
  'avgInMailsByBillingPeriod',
  'bulks',
  'calls',
  'callsTime',
  'candidatesAdded',
  'careerCashin',
  'cashin',
  'channelPartner1Name',
  'channelPartner2Name',
  'companiesAdded',
  'contactsAdded',
  'countryCode',
  'currentWeekActiveSendovers',
  'directorName',
  'drawBalance',
  'empNo',
  'fifthWeekSendouts',
  'fourthWeekSendouts',
  'haAdded',
  'healthStatus',
  'hireDate',
  'idFortpac',
  'inMailsByBillingPeriod',
  'lastFiveWeeksSendouts',
  'lastPlacementDate',
  'lastWeekSendouts',
  'location',
  'managerEmpNo',
  'managerName',
  'newLivePosts',
  'notConnectedCalls',
  'notSignedCompaniesJo',
  'outstandingBillings',
  'peopleReached',
  'production',
  'recruitedBy',
  'recruiterName',
  'signedCompaniesJo',
  'soPlRatio',
  'teamSupervisorsNames',
  'thirdWeekSendouts',
  'thisWeekSendouts',
  'todayLivePosts',
  'totalCalls',
  'trackingProduction',
  ...FEEDBACK_NOTE_METRICS,
  ...FEEDBACK_ACTIVITY_METRICS,
  ...TRACKING_CASH_IN_METRICS
];

export const METRICS_DESCRIPTION = [
  COPIES.footer.metrics.inMails,
  COPIES.footer.metrics.SOtoPL,
  COPIES.footer.metrics.calls,
  COPIES.footer.metrics.posts
];

export const extraFilters = [
  {
    name: 'rangeByCurrentWeekSendouts',
    paramName: 'currentWeekSendouts',
    title: COPIES.filters.sendouts.currentWeek,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.currentWeek
  },
  {
    name: 'rangeByLastWeekSendouts',
    paramName: 'lastWeekSendouts',
    title: COPIES.filters.sendouts.lastWeek,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.lastWeek
  },
  {
    name: 'rangeByThirdWeekSendouts',
    paramName: 'thirdWeekSendouts',
    title: COPIES.filters.sendouts.previousWeekTwo,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.previousWeekTwo
  },
  {
    name: 'rangeByFourthWeekSendouts',
    paramName: 'fourthWeekSendouts',
    title: COPIES.filters.sendouts.previousWeekThree,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.previousWeekThree
  },
  {
    name: 'rangeByFifthWeekSendouts',
    paramName: 'fifthWeekSendouts',
    title: COPIES.filters.sendouts.previousWeekFour,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.previousWeekFour
  },
  {
    name: 'rangeBySendoutPlacementRatio',
    paramName: 'sendoutToPlacementRatio',
    title: COPIES.filters.sendouts.placementRatio,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.placementRatio
  },
  {
    name: 'rangeByActiveSendouts',
    paramName: 'activeSendouts',
    title: COPIES.filters.sendouts.active,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.active
  },
  {
    name: 'rangeByLastFiveWeeksSendouts',
    paramName: 'lastFiveWeeksSendouts',
    title: COPIES.filters.sendouts.lastFiveWeeks,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.lastFiveWeeks
  },
  {
    name: 'rangeByCurrentWeekSendovers',
    paramName: 'currentWeekActiveSendovers',
    title: COPIES.filters.sendouts.currentWeekSendovers,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.currentWeekSendovers
  },
  {
    name: 'rangeByActiveSendovers',
    paramName: 'activeSendovers',
    title: COPIES.filters.sendouts.activeSendovers,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.activeSendovers
  },
  {
    name: 'rangeByOutstandingBillings',
    paramName: 'outstandingBillings',
    title: COPIES.filters.financial.outstandingBillings,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.outstandingBillings,
    inputType: 'currency'
  },
  {
    name: 'rangeByDrawBalance',
    paramName: 'drawBalance',
    title: COPIES.filters.financial.drawBalance,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.drawBalance,
    inputType: 'currency'
  },
  {
    name: 'rangeByAvgFeePercentage',
    paramName: 'avgFeePercentage',
    title: COPIES.filters.financial.avgFeePercentage,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.avgFeePercentage,
    inputType: 'percentage'
  },
  {
    name: 'rangeByAvgFee',
    paramName: 'avgFeeMoney',
    title: COPIES.filters.financial.avgFee,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.avgFee,
    inputType: 'currency'
  },
  {
    name: 'rangeByCashin',
    paramName: 'cashin',
    title: COPIES.filters.financial.cashin,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.cashin,
    inputType: 'currency'
  },
  {
    name: 'rangeByCareerCashin',
    paramName: 'careerCashin',
    title: COPIES.filters.financial.careerCashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.careerCashIn,
    inputType: 'currency'
  },
  {
    name: 'rangeByYearlyProduction',
    paramName: 'production',
    title: COPIES.filters.financial.yearlyProduction,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.yearlyProduction,
    inputType: 'currency'
  },
  {
    name: 'rangeByTrackingProduction',
    paramName: 'trackingProduction',
    title: COPIES.filters.financial.trackingProduction,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.trackingProduction,
    inputType: 'currency'
  },
  {
    name: 'rangeByTrackingCashIn',
    paramName: 'trackingCashIn',
    title: COPIES.filters.financial.trackingCashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.trackingCashIn,
    inputType: 'currency',
    showWhen: () => hasFeatureFlag(FeatureFlags.CommandCenterTrackingCashIn)
  },
  {
    name: 'rangeByInMailsByBillingPeriod',
    paramName: 'inMailsByBillingPeriod',
    title: COPIES.filters.metrics.billingPeriodInMails,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.billingPeriodInMails
  },
  {
    name: 'rangeByInMails',
    paramName: 'inMails',
    title: COPIES.filters.metrics.inMails,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.inMails
  },
  {
    name: 'rangeByBulks',
    paramName: 'bulks',
    title: COPIES.filters.metrics.bulks,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.bulks
  },
  {
    name: 'rangeByPeopleReached',
    paramName: 'peopleReached',
    title: COPIES.filters.metrics.peopleReached,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.peopleReached
  },
  {
    name: 'rangeByPosts',
    paramName: 'posts',
    title: COPIES.filters.metrics.posts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.posts
  },
  {
    name: 'rangeByNewLivePosts',
    paramName: 'newLivePosts',
    title: COPIES.filters.metrics.newLivePosts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.newLivePosts
  },
  {
    name: 'rangeByPhoneTime',
    paramName: 'phoneTime',
    title: COPIES.filters.metrics.phoneTime,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.phoneTime
  },
  {
    name: 'rangeByConnectedCalls',
    paramName: 'connectedCalls',
    title: COPIES.filters.metrics.connectedCalls,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.connectedCalls
  },
  {
    name: 'rangeByNotConnectedCalls',
    paramName: 'notConnectedCalls',
    title: COPIES.filters.metrics.notConnectedCalls,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.notConnectedCalls
  },
  {
    name: 'rangeByTotalCalls',
    paramName: 'totalCalls',
    title: COPIES.filters.metrics.totalCalls,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.totalCalls
  },
  {
    name: 'lastFeedbackActivityTypes',
    paramName: 'lastFeedbackActivityTypes',
    title: COPIES.filters.recruiters.lastSupportActivity,
    type: FilterType.Select,
    options: SUPPORT_ACTIVITIES,
    renderOption: OptionRenderers.title
  },
  {
    name: 'lastFeedbackActivityDate',
    paramName: 'lastFeedbackActivityDate',
    title: COPIES.filters.recruiters.lastSupportActivityDate,
    type: 'date',
    disableTimeRange: true
  },
  {
    name: 'recruitedBy',
    paramName: 'recruitedBy',
    title: 'Recruited By',
    type: FilterType.Input
  },
  {
    name: 'channelPartnersEmpNumbers',
    paramName: 'channelPartners',
    title: COPIES.filters.recruiters.channelPartner,
    type: FilterType.Autocomplete,
    displayKey: 'full_name',
    url: `${Endpoints.Users}?extraColumns=external_employee_id`,
    multiple: true,
    idKey: 'external_employee_id'
  },
  {
    name: 'rangeByNotSignedCompaniesJobOrders',
    paramName: 'notSignedCompaniesJo',
    title: COPIES.filters.inventory.notSignedCompaniesJobOrders,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.notSignedCompaniesJobOrders
  },
  {
    name: 'rangeBySignedCompaniesJobOrders',
    paramName: 'signedCompaniesJo',
    title: COPIES.filters.inventory.signedCompaniesJobOrders,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.signedCompaniesJobOrders
  },
  {
    name: 'rangeByCandidates',
    paramName: 'candidatesAdded',
    title: COPIES.filters.inventory.candidates,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.candidates
  },
  {
    name: 'rangeByContacts',
    paramName: 'contactsAdded',
    title: COPIES.filters.inventory.contacts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.contacts
  },
  {
    name: 'rangeByHiringAuthorities',
    paramName: 'haAdded',
    title: COPIES.filters.inventory.hiringAuthorities,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.hiringAuthorities
  },
  {
    name: 'rangeByCompanies',
    paramName: 'companiesAdded',
    title: COPIES.filters.inventory.companies,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.companies
  },
  {
    name: 'countryCode',
    paramName: 'countryCode',
    title: COPIES.filters.recruiters.country,
    type: FilterType.Select,
    options: COUNTRY_CODES,
    renderOption: OptionRenderers.title
  }
];
